<template>
	<n-tag v-if="days !== null" :round :color :size class="text-fs-sm font-medium">
		<span v-html="label"></span>
	</n-tag>
</template>

<script setup lang="ts">
import type { I18nString } from "@/lang/config"
import type { PatientMeta } from "@/types/patient"
import { NTag } from "naive-ui"
import { computed } from "vue"
import { useI18n } from "vue-i18n"

const { meta, size } = defineProps<{
	meta: PatientMeta
	round?: boolean
	size?: "small" | "medium" | "large" | "tiny"
}>()

const { t } = useI18n()

const days = computed<number | null>(() => meta.days_from_delivery ?? null)

const label = computed(() => {
	if (days.value === null) return ""

	if (days.value >= 0) {
		return t<I18nString>("pregnancies.common.delivery_days_future", { count: days.value })
	}

	// days.value < 0
	const absDays = Math.abs(days.value)
	return t<I18nString>("pregnancies.common.delivery_days_past", { count: absDays })
})

const color = computed(() => {
	if (days.value === null) {
		return undefined
	}

	if (days.value < 0) {
		return {
			color: "var(--error-400-color)",
			borderColor: "var(--error-500-color)",
			textColor: "var(--error-600-color)"
		}
	}

	if (days.value > 0) {
		return {
			color: "var(--info-400-color)",
			borderColor: "var(--info-500-color)",
			textColor: "var(--info-600-color)"
		}
	}

	if (days.value === 0) {
		return {
			color: "var(--warning-400-color)",
			borderColor: "var(--warning-500-color)",
			textColor: "var(--warning-600-color)"
		}
	}

	return undefined
})
</script>
